import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, message, notification, PageHeader, Table } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';

import moment from 'moment';
import Column from '../../helpers/columns';
import {
  generateReport,
  getReportDefinition,
  listReports,
} from '../../services/reports/reports';
import ReportWindow from './ReportWindow';

const ReportsList = () => {
  const { t } = useTranslation();

  const [reports, setReports] = useState(null);
  const [reportDefinition, setReportDefinition] = useState(null);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    listReports()
      .then((list) => setReports(list))
      .catch((err) => {
        console.error(err);
        setReports([]);

        if (err?.status === 404) {
          notification.error({
            message: t('errors.404.dataNotFound'),
          });
        } else {
          notification.error({
            message: t('errors.reportListLoadError'),
          });
        }
      });
  }, [t]);

  const loadReport = useCallback((report) => {
    getReportDefinition(report.uri)
      .then((res) => setReportDefinition(res))
      .catch((err) => {
        console.error(err);
        notification.error({
          message: `${err}`,
        });
      });
  }, []);

  const columns = useMemo(
    () => [
      Column.text('label', t('pages.reports.label'), {
        width: 120,
        filter: true,
      }),
      Column.text('description', t('pages.reports.description'), {
        width: 300,
        filter: true,
      }),
      Column.actions(
        t('table.actions'),
        (record) => (
          <span style={{ textAlign: 'right' }}>
            <Button
              shape="circle"
              type="link"
              icon={<PrinterOutlined />}
              onClick={() => loadReport(record)}
            />
          </span>
        ),
        { width: 60 },
      ),
    ],
    [loadReport, t],
  );

  const adjustReportDates = (report) => {
    const adjusted = { ...report };

    if (adjusted.START) {
      adjusted.START = moment(adjusted.START)
        .startOf('day')
        .milliseconds(0)
        .toDate();
    }

    if (adjusted.END) {
      adjusted.END = moment(adjusted.END).endOf('day').milliseconds(0).toDate();
    }

    return adjusted;
  };

  const handlePrint = useCallback(
    (report) => {
      const { label } = reportDefinition;
      message.loading({
        content: t('pages.reports.generatingReport'),
        key: 'raport',
        duration: 0,
        className: 'card-message',
      });
      setButtonDisabled(true);

      const adjustedReport = adjustReportDates(report);
      const { _format } = adjustedReport;

      generateReport(
        reportDefinition.uri,
        adjustedReport,
        `${label}.${_format}`,
      )
        .then(() => {
          message.loading({
            content: t('pages.reports.generatingReport'),
            key: 'raport',
            duration: 1,
            className: 'card-message',
          });
          notification.success({
            message: t('pages.reports.generationSuccess'),
          });
          setButtonDisabled(false);
        })
        .catch((err) => {
          message.loading({
            content: t('pages.reports.generatingReport'),
            key: 'raport',
            duration: 1,
            className: 'card-message',
          });
          console.error(err);
          notification.error({
            message: t('errors.noReportsWithFilters'),
          });
          setButtonDisabled(false);
        });
    },
    [reportDefinition, t],
  );

  return (
    <>
      <PageHeader title={t('pages.reports._')} />
      <Table
        columns={columns}
        rowKey="uri"
        // loading={reports == null}
        pagination={false}
        dataSource={reports}
        scroll={{ x: 700, y: 700 }}
      />
      <ReportWindow
        report={reportDefinition}
        onPrint={handlePrint}
        buttonDisabled={buttonDisabled}
        onClose={() => setReportDefinition(null)}
      />
    </>
  );
};

export default ReportsList;
